import { type ReactNode, type CSSProperties, useEffect } from "react";
import { useQuizData } from "@/hooks/useQuizData";
import type { QuizTheme } from "@/types/api.types";

interface ThemeProviderProps {
  children: ReactNode;
}

const FONT_STACKS: Record<string, string> = {
  roboto: '"Roboto", ui-sans-serif, system-ui',
  poppins: '"Poppins", ui-sans-serif, system-ui',
  open_sans: '"Open Sans", ui-sans-serif, system-ui',
};

const getFontStack = (fontFamily?: string) => {
  const key = fontFamily?.trim().toLowerCase();
  return FONT_STACKS[key ?? ""] ?? '"Inter", ui-sans-serif, system-ui';
};

const generateThemeStyles = (theme: QuizTheme): CSSProperties => {
  return {
    "--primary": theme.colors.primary,
    "--secondary": theme.colors.secondary,
    "--bg-light": theme.colors.background_light,
    "--bg-dark": theme.colors.background_dark,
    "--divider": theme.colors.divider,
    "--dots": theme.colors.dots,
    "--cta": theme.colors.cta,
    "--cta-hover": theme.colors.cta_hover,
    "--cta-border": theme.colors.cta_border,
    "--text-dark": theme.colors.text_dark,
    "--gradient-1": theme.colors.gradient_1,
    "--gradient-2": theme.colors.gradient_2,
    "--app-font-sans": getFontStack(theme.fontFamily),
  } as CSSProperties;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { data } = useQuizData();

  useEffect(() => {
    if (!data?.theme) return;

    const vars = generateThemeStyles(data.theme) as Record<string, string>;
    const root = document.documentElement;

    Object.entries(vars).forEach(([k, v]) =>
      root.style.setProperty(k, String(v)),
    );

    return () => {
      Object.keys(vars).forEach((k) => root.style.removeProperty(k));
    };
  }, [data?.theme]);

  if (!data?.theme) {
    return <>{children}</>;
  }

  const themeStyles = generateThemeStyles(data.theme);

  return (
    <div style={themeStyles} className="contents">
      {children}
    </div>
  );
};
